<script setup lang="ts">
import { useRegistrationStore } from '~/stores/registration'
import { useModal } from '~/composables/useModal'
import type BugReportForm from '~/components/organisms/forms/BugReportForm.vue'

type ErrorReportModalEmits = {
  resetCookies: []
}

const emits = defineEmits<ErrorReportModalEmits>()

const isOpen = defineModel<boolean>('open', { required: true })

const registrationStore = useRegistrationStore()
const authStore = useAuthStore()

const bugReportForm = useTemplateRef<InstanceType<typeof BugReportForm>>('bugReportForm')

const { t } = useI18n('components.organisms.modal.error-reporting-modal')

const { modalIsOpen: bugReportIsOpen, closeModal: closeBugReport } = useModal()

const resetRegistrationStore = () => {
  registrationStore.resetState()
}

const openBugReport = () => {
  bugReportIsOpen.value = !bugReportIsOpen.value

  nextTick(() => {
    if (bugReportForm.value?.$el) {
      const scrollBehaviour = getAnimationBehaviour()
      bugReportForm.value.$el.scrollIntoView({ behavior: scrollBehaviour })
    }
  })
}
</script>

<template>
  <VModal v-model:open="isOpen" title=".title">
    <template #content>
      <section class="section p-2">
        <div class="prose text-center">
          <!-- Error reporting -->
          <p>
            {{ t('.description') }}
          </p>

          <!-- Cookies -->
          <p class="mt-4">
            {{ t('.delete-cookies-description') }}
          </p>
          <VButton color="danger" width="full" @click="emits('resetCookies')">
            {{ t('.delete-cookies-button') }}
          </VButton>

          <!-- Registration store -->
          <p class="mt-4">
            {{ t('.reset-store-description') }}
          </p>
          <VButton color="danger" width="full" @click="resetRegistrationStore">
            {{ t('.reset-store-button') }}
          </VButton>

          <!-- Bug report -->
          <template v-if="authStore.hasAdminRole">
            <p class="mt-4">
              {{ t('.bug-report-description') }}
            </p>
            <VButton color="info" width="full" icon-right="fa-bug" @click="openBugReport">
              {{ t('.bug-report-button') }}
            </VButton>
          </template>
        </div>

        <BugReportForm
          v-show="bugReportIsOpen"
          ref="bugReportForm"
          @close="closeBugReport"
        />
      </section>
    </template>

    <template #actions>
      <VButton
        v-if="bugReportIsOpen"
        type="submit"
        :form="bugReportForm?.formProps.id"
        :disabled="!bugReportForm?.meta.valid"
        :loading="bugReportForm?.isLoading"
        width="full"
        color="success"
      >
        {{ t('buttons.submit') }}
      </VButton>
    </template>
  </VModal>
</template>

<style scoped></style>
